import { formatDate } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, LOCALE_ID, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { disabled, form, FormField, required, submit, validate, validateTree } from '@angular/forms/signals';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatError, MatFormFieldModule } from '@angular/material/form-field';
import { TimeButtonsComponent } from '@app/components/time-reports/time-report-dialog/time-buttons/time-buttons.component';
import {
  ReportSeriesDailyRange,
  ReportSeriesForm
} from '@app/shared/components/report-entry/report-series/report-series-form';
import { ReportSeriesSectionComponent } from '@app/shared/components/report-entry/report-series/report-series-section.component';
import { ReportSummaryComponent } from '@app/shared/components/report-entry/report-summary/report-summary.component';
import { TimeReportsActionsService } from '@app/shared/services/actions/time-reports-actions.service';
import { ActiveCompanyService } from '@app/shared/services/active-company.service';
import { AuthService } from '@app/shared/services/auth.service';
import { CompaniesDataService } from '@app/shared/services/companies-data.service';
import { ModifyAbsenceReportModel } from '@app/shared/services/dtos/reports/modify-absence-report.model';
import { isWritableAbsenceType, WritableAbsenceType } from '@app/shared/services/dtos/reports/writable-absence-type';
import { TimeReportsDataService } from '@app/shared/services/time-reports-data.service';
import { UsersDataService } from '@app/shared/services/users-data.service';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { isAbsenceReport } from '@app/shared/types/models/reports/absence-report';
import { SeriesWeekday } from '@app/shared/types/models/reports/time-report-series';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { TimePickerValue } from '@app/shared/types/models/shared/time-picker-value';
import { TimeReportingType } from '@app/shared/types/models/shared/time-reporting-type';
import { User } from '@app/shared/types/models/user/user.model';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';
import { UiChoiceGroupComponent } from '@app/shared/ui/choice-group/ui-choice-group.component';
import { UiDateRangePickerComponent } from '@app/shared/ui/date-range-picker/ui-date-range-picker.component';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiTextareaComponent } from '@app/shared/ui/textarea/ui-textarea.component';
import { UiTimePickerComponent } from '@app/shared/ui/time-picker/ui-time-picker.component';
import { ProffeoUiChoiceOption } from '@app/shared/ui/types/choice-group.types';
import { ProffeoUiDateRange } from '@app/shared/ui/types/date-picker.types';
import { dailyRangeMinutes } from '@app/shared/utils/time-report-series.utils';
import { amountAndUnitToDuration, durationToHours } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AbsenceReportDialogData } from './absence-report-dialog-data';
import { absenceTotalHours, countAbsenceDays, isSameLocalDay } from './absence-report-dialog.summary';
import { absenceTypeTranslationKey, WRITABLE_ABSENCE_TYPE_OPTIONS } from './absence-report-dialog.types';
import { AbsenceDateRangeErrorKind, toIsoDateTime, validateAbsenceDateRange } from './absence-report-dialog.validators';
import {
  inferWeekendFlags,
  pickerDateForInstant,
  weekendDaysInLocalPickerRange
} from './absence-report-dialog.weekend';

const DEFAULT_DURATION_HOURS = 7.5;
function durationHoursToPicker(hours: number): TimePickerValue {
  const totalMinutes = Math.round(Math.max(0, hours) * 60);
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

function durationPickerToHours(value: TimePickerValue): number {
  return value.hours + value.minutes / 60;
}

/** Canonical absence dialog model — no mirrored date/duration duplicates. */
export interface AbsenceReportFormModel {
  companyId: string;
  userId: string;
  userName: string;
  absenceType: WritableAbsenceType | null;
  dateRange: ProffeoUiDateRange;
  durationTime: TimePickerValue;
  includeSaturday: boolean | null;
  includeSunday: boolean | null;
  /** Series only: Manual (duration per day) or Range (daily from/to). */
  entryMode: TimeReportingType.MANUAL | TimeReportingType.RANGE;
  dailyStartTime: TimePickerValue;
  dailyEndTime: TimePickerValue;
  description: string;
  comments: string;
}

@Component({
  selector: 'proffeo-absence-report-dialog',
  templateUrl: './absence-report-dialog.component.html',
  imports: [
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatError,
    FormField,
    TranslatePipe,
    UiDialogComponent,
    UiInputComponent,
    UiSelectComponent,
    UiDateRangePickerComponent,
    UiTimePickerComponent,
    UiTextareaComponent,
    UiButtonComponent,
    TimeButtonsComponent,
    ReportSummaryComponent,
    ReportSeriesSectionComponent,
    UiChoiceGroupComponent
  ]
})
export class AbsenceReportDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<AbsenceReportDialogComponent, boolean>);
  private readonly modalData: AbsenceReportDialogData = inject(MAT_DIALOG_DATA);
  private readonly timeReportsDataService = inject(TimeReportsDataService);
  private readonly timeReportsActionsService = inject(TimeReportsActionsService);
  private readonly companiesDataService = inject(CompaniesDataService);
  private readonly usersDataService = inject(UsersDataService);
  private readonly activeCompanyService = inject(ActiveCompanyService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LOCALE_ID);
  private readonly isCreate = !this.modalData.report;
  private weekendPresence = { saturday: false, sunday: false };
  private previousCompanyId = '';

  protected readonly isSaving = signal(false);
  protected readonly isEdit = computed(() => !!this.modalData.report);
  protected readonly canSelectUser = computed(
    () => this.isCreate && this.timeReportsActionsService.isTimeReportAdmin(this.authService.currentUser()?.email)
  );
  protected readonly unsupportedExistingType = signal<string | undefined>(undefined);
  protected readonly writableTypeOptions = WRITABLE_ABSENCE_TYPE_OPTIONS;
  protected readonly dialogTitleKey = computed(() =>
    this.isEdit() ? 'time-reports.absence-dialog.title-edit' : 'time-reports.absence-dialog.title-create'
  );

  protected readonly model = signal<AbsenceReportFormModel>(this.buildInitialModel());

  /** Shared multi-day series option (test API only, opt-in, create only). */
  protected readonly series = new ReportSeriesForm({
    canUse: (): boolean => this.isCreate,
    dailyRange: (): ReportSeriesDailyRange | undefined =>
      this.model().entryMode === TimeReportingType.RANGE
        ? { from: this.model().dailyStartTime, to: this.model().dailyEndTime }
        : undefined,
    initialRange: (): ProffeoUiDateRange => ({ ...this.model().dateRange })
  });
  protected readonly entryModeOptions: readonly ProffeoUiChoiceOption<TimeReportingType>[] = [
    { value: TimeReportingType.MANUAL, label: 'time-reports-form-modal-manual' },
    { value: TimeReportingType.RANGE, label: 'time-reports-form-modal-range' }
  ];
  /** Range entry exists only inside an active series; the regular absence is always Manual. */
  protected readonly isRangeEntry = computed(
    () => this.series.active() && this.model().entryMode === TimeReportingType.RANGE
  );

  protected readonly form = form(this.model, path => {
    required(path.companyId, {
      when: () => this.isCreate,
      message: 'ui-forms-this-field-is-required'
    });
    required(path.userId, {
      when: () => this.canSelectUser(),
      message: 'ui-forms-this-field-is-required'
    });
    required(path.absenceType, { message: 'ui-forms-this-field-is-required' });
    required(path.description, { message: 'errors.required' });
    disabled(path.userId, ({ valueOf }) => !valueOf(path.companyId));
    disabled(path.companyId, () => !this.isCreate);
    disabled(path.userName);

    validate(path.dateRange, ({ value }) => {
      if (this.series.active()) {
        // The series section owns the dates (and the 31-day limit) while the series is on.
        return undefined;
      }
      const range = value();
      if (!range.start) {
        return { kind: 'fromRequired' };
      }
      if (!range.end) {
        return { kind: 'toRequired' };
      }

      const fromDay = new Date(range.start.getFullYear(), range.start.getMonth(), range.start.getDate()).getTime();
      const toDay = new Date(range.end.getFullYear(), range.end.getMonth(), range.end.getDate()).getTime();
      if (toDay < fromDay) {
        return { kind: 'invalidDateRange' };
      }

      return undefined;
    });

    validate(path.durationTime, ({ value }) => {
      if (this.isRangeEntry()) {
        return undefined;
      }
      const durationHours = durationPickerToHours(value());
      if (!(typeof durationHours === 'number' && durationHours > 0)) {
        return { kind: 'durationInvalid' };
      }
      return undefined;
    });

    validateTree(path, ({ value }) => {
      if (this.series.active()) {
        return undefined;
      }
      const current = value();
      const durationHours = durationPickerToHours(current.durationTime);
      const errorKind = validateAbsenceDateRange({
        dateFrom: current.dateRange.start,
        dateTo: current.dateRange.end,
        durationHours,
        includeSaturday: current.includeSaturday,
        includeSunday: current.includeSunday
      });
      if (
        !errorKind ||
        errorKind === 'fromRequired' ||
        errorKind === 'toRequired' ||
        errorKind === 'invalidDateRange' ||
        errorKind === 'durationInvalid'
      ) {
        // Those kinds are already reported on dateRange / durationTime fields.
        return undefined;
      }
      return { kind: errorKind };
    });
  });

  protected readonly companyOptions = computed((): DropdownItem<string>[] => {
    const userId = this.authService.currentUser()?.id;
    if (!userId) {
      return [];
    }
    return (this.companiesDataService.getUserCompanies(userId)() ?? []).map(company => ({
      value: company.id,
      label: company.name ?? company.id
    }));
  });

  protected readonly userDisplayName = computed(() => this.model().userName);
  protected readonly partyCompanyOptions = computed((): DropdownItem<string>[] => {
    const reportCompany = this.modalData.report?.company?.();
    if (reportCompany?.id) {
      return [{ value: reportCompany.id, label: reportCompany.name ?? reportCompany.id }];
    }
    return this.companyOptions();
  });
  protected readonly selectedCompanyId = computed(() => this.model().companyId || null);
  protected readonly companyUserOptions = computed((): DropdownItem<string>[] => {
    if (!this.canSelectUser()) {
      return [];
    }
    const companyId = this.selectedCompanyId();
    if (!companyId) {
      return [];
    }
    return (this.usersDataService.getCompanyUsers(companyId)() ?? []).map(user => ({
      value: user.id,
      label: user.fullName
    }));
  });

  protected readonly saturdayChecked = computed(() => this.model().includeSaturday === true);
  protected readonly saturdayIndeterminate = computed(() => this.model().includeSaturday === null);
  protected readonly sundayChecked = computed(() => this.model().includeSunday === true);
  protected readonly sundayIndeterminate = computed(() => this.model().includeSunday === null);
  protected readonly unknownWeekend = computed(() => {
    const errors = this.form().errorSummary();
    return errors.some(error => error.kind === 'unknownSaturday' || error.kind === 'unknownSunday');
  });
  protected readonly durationInvalid = computed(() => {
    const root = this.form();
    return root.touched() && this.hasFormError('durationInvalid');
  });
  protected readonly dateRangeErrorKey = computed((): string => {
    if (!this.form().touched() && !this.form.dateRange().touched()) {
      return '';
    }
    if (this.hasFormError('invalidDateRange')) {
      return 'time-reports.absence-dialog.invalid-date-range';
    }
    if (this.hasFormError('fromRequired') || this.hasFormError('toRequired')) {
      return 'ui-forms-this-field-is-required';
    }
    return '';
  });

  protected readonly summaryPeriodFrom = computed(() =>
    this.series.active() ? this.series.model().dateRange.start : this.model().dateRange.start
  );
  protected readonly summaryPeriodTo = computed(() =>
    this.series.active() ? this.series.model().dateRange.end : this.model().dateRange.end
  );
  protected readonly summaryIsSingleDay = computed(() =>
    isSameLocalDay(this.summaryPeriodFrom(), this.summaryPeriodTo())
  );
  protected readonly summaryDurationHours = computed(() => {
    if (this.isLegacyMultiDayEdit()) {
      return DEFAULT_DURATION_HOURS;
    }
    if (this.isRangeEntry()) {
      // A series break is deducted for the preview only (Range writes the gross from/to).
      return Math.max(0, this.rangeEntryHours() - this.series.effectiveBreakMinutes() / 60);
    }
    return durationPickerToHours(this.model().durationTime);
  });
  /** Legacy multi-day edit: the backend materializes a fixed 7.5h per day and ignores Duration. */
  protected readonly isLegacyMultiDayEdit = computed(
    () => this.isEdit() && !this.summaryIsSingleDay() && !!this.summaryPeriodTo()
  );
  protected readonly summaryDays = computed(() => {
    if (this.series.active()) {
      return this.series.occurrenceDates().length;
    }
    const value = this.model();
    return countAbsenceDays(
      value.dateRange.start,
      value.dateRange.end,
      value.includeSaturday === true,
      value.includeSunday === true
    );
  });
  protected readonly summaryTotalHours = computed(() =>
    absenceTotalHours(this.summaryDays(), this.summaryDurationHours())
  );
  protected readonly summaryPeriodLabel = computed(() => {
    const from = this.summaryPeriodFrom();
    const to = this.summaryPeriodTo();
    if (!from || !to) {
      return '';
    }
    const fromLabel = formatDate(from, 'd MMM y', this.locale);
    const toLabel = formatDate(to, 'd MMM y', this.locale);
    return this.summaryIsSingleDay() ? fromLabel : `${fromLabel} – ${toLabel}`;
  });
  protected readonly summaryAbsenceTypeKey = computed(() => {
    const absenceType = this.model().absenceType;
    return absenceTypeTranslationKey(absenceType || undefined) ?? '';
  });
  protected readonly summaryDurationMinutes = computed(() => Math.round(this.summaryDurationHours() * 60));
  protected readonly summaryTotalMinutes = computed(() => Math.round(this.summaryTotalHours() * 60));

  protected readonly saveDisabled = computed(() => this.isSaving());

  public constructor() {
    const currentUser = this.authService.currentUser();
    const userId = currentUser?.id;
    if (userId) {
      this.companiesDataService.loadUserCompanies(userId);
    }

    const report = this.modalData.report;
    const existingType = report?.absenceType;
    const writableType = existingType && isWritableAbsenceType(existingType) ? existingType : null;
    this.unsupportedExistingType.set(
      existingType && !writableType ? absenceTypeTranslationKey(existingType) : undefined
    );

    const initialCompanyId = this.model().companyId;
    this.previousCompanyId = initialCompanyId;
    this.weekendPresence = this.currentWeekendPresence();

    if (this.canSelectUser() && initialCompanyId) {
      this.usersDataService.loadCompanyUsers(initialCompanyId);
    }

    effect(() => {
      const range = this.model().dateRange;
      untracked(() => this.onRangeChanged(range));
    });

    effect(() => {
      const companyId = this.model().companyId;
      untracked(() => this.handleCompanyIdChange(companyId));
    });

    effect(() => {
      const userId = this.model().userId;
      untracked(() => this.syncUserNameFromId(userId));
    });
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    if (this.isSaving()) {
      return;
    }

    this.series.markSubmitted();
    if (!this.series.valid()) {
      this.form().markAsTouched();
      return;
    }

    void submit(this.form, {
      action: async () => {
        const body = this.buildRequest();
        if (!body) {
          return [{ kind: 'requestInvalid', message: 'Unable to build absence request' }];
        }

        this.isSaving.set(true);
        try {
          const reportId = this.modalData.report?.id;
          const selectedUserId = this.model().userId;
          const currentUserId = this.authService.currentUser()?.id;
          const companyId = this.model().companyId;
          const otherUserId =
            this.canSelectUser() && selectedUserId && selectedUserId !== currentUserId ? selectedUserId : undefined;
          // A series resolving to a single day keeps the regular single POST (buildRequest uses that day).
          const series = !reportId && this.series.isMultiDay() ? this.series.buildSeries(body.duration) : undefined;
          const request$ = series
            ? this.timeReportsDataService.addAbsenceReportSeries(companyId, body, series, otherUserId)
            : reportId
              ? this.timeReportsDataService.updateAbsenceReport(reportId, body)
              : this.canSelectUser() && selectedUserId && selectedUserId !== currentUserId
                ? this.timeReportsDataService.addAbsenceReportForUser(companyId, selectedUserId, body)
                : this.timeReportsDataService.addAbsenceReport(companyId, body);

          await firstValueFrom(request$);
          this.dialogRef.close(true);
          return undefined;
        } catch {
          return undefined;
        } finally {
          this.isSaving.set(false);
        }
      }
    });
  }

  protected onDurationPresetSelected(value: TimePickerValue): void {
    this.model.update(current => ({ ...current, durationTime: { ...value } }));
  }

  protected onSaturdayChange(event: MatCheckboxChange): void {
    this.model.update(current => ({ ...current, includeSaturday: event.checked }));
    this.form.includeSaturday().markAsTouched();
  }

  protected onSundayChange(event: MatCheckboxChange): void {
    this.model.update(current => ({ ...current, includeSunday: event.checked }));
    this.form.includeSunday().markAsTouched();
  }

  protected onCompanyChange(companyId: string | null): void {
    this.model.update(current => ({ ...current, companyId: companyId ?? '' }));
  }

  protected openUserPicker(): void {
    const companyId = this.model().companyId;
    if (!this.canSelectUser() || !companyId) {
      return;
    }

    const selectUser = this.modalData.selectUser;
    if (!selectUser) {
      return;
    }

    selectUser(companyId, this.model().userId || undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedUser: User | undefined) => this.onUserSelected(selectedUser));
  }

  /** Test/harness helper: whether the form currently reports the given root error kind. */
  protected hasFormError(kind: AbsenceDateRangeErrorKind | string): boolean {
    return this.form()
      .errorSummary()
      .some(error => error.kind === kind);
  }

  private syncUserNameFromId(userId: string): void {
    if (!userId || !this.canSelectUser()) {
      return;
    }
    const selectedUser = (this.usersDataService.getCompanyUsers(this.model().companyId)() ?? []).find(
      user => user.id === userId
    );
    if (selectedUser && this.model().userName !== selectedUser.fullName) {
      this.model.update(current => ({ ...current, userName: selectedUser.fullName }));
    }
  }

  private onUserSelected(selectedUser: User | undefined): void {
    if (!selectedUser) {
      return;
    }

    this.model.update(current => ({
      ...current,
      userId: selectedUser.id,
      userName: selectedUser.fullName
    }));
  }

  private buildInitialModel(): AbsenceReportFormModel {
    const currentUser = this.authService.currentUser();
    const report = this.modalData.report;
    const reportUser = report?.user?.();
    const initialCompanyId = report?.company?.()?.id ?? this.activeCompanyService.activeCompanyId() ?? '';
    const existingType = report?.absenceType;
    const writableType = existingType && isWritableAbsenceType(existingType) ? existingType : null;
    const initial = this.resolveInitialRange();

    return {
      companyId: initialCompanyId,
      userId: reportUser?.id ?? currentUser?.id ?? '',
      userName: reportUser?.fullName ?? currentUser?.fullName ?? '',
      absenceType: writableType,
      dateRange: { start: initial.dateFrom, end: initial.dateTo },
      durationTime: durationHoursToPicker(initial.durationHours ?? DEFAULT_DURATION_HOURS),
      includeSaturday: initial.includeSaturday,
      includeSunday: initial.includeSunday,
      entryMode: TimeReportingType.MANUAL,
      dailyStartTime: { hours: 8, minutes: 0 },
      dailyEndTime: { hours: 16, minutes: 0 },
      description: report?.description ?? '',
      comments: report?.comments ?? ''
    };
  }

  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private resolveInitialRange(): {
    dateFrom: Date | null;
    dateTo: Date | null;
    durationHours: number | null;
    includeSaturday: boolean | null;
    includeSunday: boolean | null;
  } {
    const report = this.modalData.report;
    if (!report) {
      const today = this.startOfToday();
      return {
        dateFrom: today,
        dateTo: new Date(today.getTime()),
        durationHours: DEFAULT_DURATION_HOURS,
        includeSaturday: false,
        includeSunday: false
      };
    }

    const persistedDuration = report.duration ? durationToHours(report.duration) : null;

    const registration = report.registration;
    const rangeFrom = registration?.rangeFrom;
    const rangeTo = registration?.rangeTo;
    if (rangeFrom && rangeTo) {
      const timeZone = registration?.reportTimeZone;
      const flags = inferWeekendFlags({
        registrationId: registration?.id,
        timeZone,
        rangeFrom,
        rangeTo,
        pickerStart: this.modalData.dateFrom,
        pickerEnd: this.modalData.dateTo,
        siblingInstants: this.siblingInstants(registration?.id)
      });
      return {
        dateFrom: pickerDateForInstant(rangeFrom, timeZone),
        dateTo: pickerDateForInstant(rangeTo, timeZone),
        durationHours: persistedDuration,
        includeSaturday: flags.includeSaturday,
        includeSunday: flags.includeSunday
      };
    }

    const singleDay = report.date ?? this.modalData.dateFrom ?? this.startOfToday();
    const localDay = new Date(singleDay.getFullYear(), singleDay.getMonth(), singleDay.getDate());
    return {
      dateFrom: localDay,
      dateTo: new Date(localDay.getTime()),
      durationHours: persistedDuration,
      includeSaturday: false,
      includeSunday: false
    };
  }

  private siblingInstants(registrationId: string | undefined): Date[] {
    if (!registrationId || !this.modalData.dateFrom || !this.modalData.dateTo) {
      return [];
    }

    return (this.timeReportsDataService.getTimeReports(this.modalData.dateFrom, this.modalData.dateTo)() ?? [])
      .filter(isAbsenceReport)
      .filter(report => report.registration?.id === registrationId)
      .map(report => report.date)
      .filter((date): date is Date => !!date);
  }

  private onRangeChanged(range: ProffeoUiDateRange): void {
    const next = this.weekendPresenceForRange(range);
    if (this.isCreate) {
      if (!this.weekendPresence.saturday && next.saturday) {
        this.model.update(current => ({ ...current, includeSaturday: false }));
      }
      if (!this.weekendPresence.sunday && next.sunday) {
        this.model.update(current => ({ ...current, includeSunday: false }));
      }
    } else {
      if (!this.weekendPresence.saturday && next.saturday) {
        this.model.update(current => ({ ...current, includeSaturday: null }));
      }
      if (!this.weekendPresence.sunday && next.sunday) {
        this.model.update(current => ({ ...current, includeSunday: null }));
      }
    }

    this.weekendPresence = next;
  }

  private handleCompanyIdChange(companyId: string): void {
    if (companyId === this.previousCompanyId) {
      return;
    }
    this.previousCompanyId = companyId;

    if (!this.canSelectUser()) {
      return;
    }

    if (companyId) {
      this.usersDataService.loadCompanyUsers(companyId);
    }

    const currentUser = this.authService.currentUser();
    const selectedUserId = this.model().userId;
    if (selectedUserId && selectedUserId !== currentUser?.id) {
      this.model.update(current => ({
        ...current,
        userId: '',
        userName: ''
      }));
    }
  }

  private currentWeekendPresence(): { saturday: boolean; sunday: boolean } {
    return this.weekendPresenceForRange(this.model().dateRange);
  }

  private weekendPresenceForRange(range: ProffeoUiDateRange): { saturday: boolean; sunday: boolean } {
    const from = range.start;
    const to = range.end;
    if (!from || !to) {
      return { saturday: false, sunday: false };
    }
    return weekendDaysInLocalPickerRange(from, to);
  }

  /** Gross hours of the series Range entry (0 when the daily range is invalid). */
  private rangeEntryHours(): number {
    const value = this.model();
    return (dailyRangeMinutes(value.dailyStartTime, value.dailyEndTime) ?? 0) / 60;
  }

  private buildRequest(): ModifyAbsenceReportModel | undefined {
    const value = this.model();
    const seriesActive = this.series.active();
    const seriesRange = this.series.model().dateRange;
    const seriesDay = this.series.singleDate();
    const dateFrom = seriesActive ? (seriesDay ?? seriesRange.start) : value.dateRange.start;
    const dateTo = seriesActive ? (seriesDay ?? seriesRange.end) : value.dateRange.end;
    if (!value.absenceType || !isWritableAbsenceType(value.absenceType) || !dateFrom || !dateTo) {
      return undefined;
    }

    const fromIso = toIsoDateTime(dateFrom);
    const toIso = toIsoDateTime(dateTo);
    if (!fromIso || !toIso) {
      return undefined;
    }

    const durationHours = this.isRangeEntry() ? this.rangeEntryHours() : durationPickerToHours(value.durationTime);
    const duration = amountAndUnitToDuration(durationHours, TimeUnits.HOURS);
    if (!duration) {
      return undefined;
    }

    const comments = value.comments.trim();
    const request: ModifyAbsenceReportModel = {
      date: fromIso,
      dateFrom: fromIso,
      dateTo: toIso,
      duration,
      description: value.description.trim(),
      absenceType: value.absenceType
    };

    if (this.isEdit() && isSameLocalDay(dateFrom, dateTo)) {
      // Single-day edit uses Duration only; the update endpoint rejects equal DateFrom/DateTo.
      delete request.dateFrom;
      delete request.dateTo;
    } else if (this.isLegacyMultiDayEdit()) {
      // Legacy multi-day absence materialization owns its fixed daily duration.
      delete request.duration;
    }

    if (comments.length > 0) {
      request.comments = comments;
    }
    if (seriesActive) {
      // Weekend days are part of a series only when their weekday chip is selected.
      request.includeSaturday = this.series.isWeekdaySelected(SeriesWeekday.Saturday);
      request.includeSunday = this.series.isWeekdaySelected(SeriesWeekday.Sunday);
      return request;
    }
    if (typeof value.includeSaturday === 'boolean') {
      request.includeSaturday = value.includeSaturday;
    }
    if (typeof value.includeSunday === 'boolean') {
      request.includeSunday = value.includeSunday;
    }

    return request;
  }
}
